import React, { Component } from 'react';
import FaqSection from './FaqSection';
import './case-management.css';
import NavBar from './NavBar';
import legal1 from './assets/legal1.webp';
import legal2 from './assets/legal2.webp';
import legal3 from './assets/legal3.webp';

interface CaseManagementProps {}

interface CaseManagementState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  lightboxSrc: string | null;
}

class CaseManagement extends Component<CaseManagementProps, CaseManagementState> {
  constructor(props: CaseManagementProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      lightboxSrc: null
    };
  }

  openLightbox = (src: string) => {
    this.setState({ lightboxSrc: src });
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  render() {
    return (
      <div className="case-management-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="case-management-hero-section">
          <div className="case-management-hero-video-background">
            <video
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
              className="case-management-hero-video"
            >
              <source src={require('./assets/video/web/15273988_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="case-management-hero-content">
            <div className="case-management-hero-text">
              <span className="case-management-hero-eyebrow">pEntities-Legal Module</span>
              <h1 className="case-management-hero-title">
                Command Every Matter.<br />Enforce Every Deadline.
              </h1>
              <p className="case-management-hero-subtitle">
                Matters aren't lost because nobody cares! They're lost because the deadline lived in one
                calendar, the contract in a folder, the email in an inbox, and nothing connected them.
                Now they're connected, and the dates watch themselves.
              </p>
              <div className="case-management-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="case-management-hero-cta-btn"
                >
                  Schedule a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description Section */}
        <section id="overview" className="lg-gendesc-section">
          <div className="case-management-container">
            <div className="lg-gendesc-content">
              <span className="lg-gendesc-label">General Description</span>
              <h2 className="lg-gendesc-title">Every matter, contract and deadline in one place</h2>
              <p>
                Legal teams don't lose matters because nobody cares. They lose them because the deadline lived in
                one person's calendar, the contract lived in a folder, the email lived in an inbox  and nothing
                connected them.
              </p>
              <p>
                <b>One record, inside Teams.</b> pEntities Legal brings the whole picture together: matters and
                their contracts, the risks and compliance obligations attached to them, the documents and emails
                that evidence them, the tasks that move them forward, and the dates that must not be missed.
              </p>
              <p>
                <b>Not another silo.</b> Documents stay in SharePoint, email stays in Outlook, tasks are real
                Microsoft Planner tasks, and the whole thing runs inside your own tenant. What pEntities adds is
                the legal structure on top  and an engine that watches the dates and acts on them without being
                asked.
              </p>
              <p>
                <b>The bottom line.</b> A complete matter and contract register on the Microsoft 365 you already
                own. Deadlines chasing themselves, exposure visible across the portfolio, and no new platform to
                license, migrate to or secure. Available in seven languages.
              </p>
            </div>
          </div>
        </section>

        {/* The Legal Module dark */}
        <section id="legal-module" className="lg-dark-section">
          <div className="case-management-container">
            <span className="lg-dark-eyebrow">The Legal Module</span>
            <h2 className="lg-dark-title">Matters, contracts, obligations  and the deadlines that govern them</h2>
            <p className="lg-dark-lead">
              A full case register with the commercial substance, the evidence and the dates attached  and an
              engine that starts work when a date approaches.
            </p>

            <div className="lg-dark-block">
              <h3>1 · One home for every matter</h3>
              <figure className="lg-figure is-float" onClick={() => this.openLightbox(legal1)}>
                <img loading="lazy" decoding="async" src={legal1} alt="The matter register" />
                <figcaption>The matter register total cases, open work, high and critical priority and overdue deadlines, above the case list.</figcaption>
              </figure>
              <p>
                A matter is a single structured record  not a folder name and a shared mailbox.
              </p>
              <ul>
                <li><b>Classified the way legal teams think</b>  type (dispute, litigation, employment, IP, patent, regulatory, corporate), category, sub-type, jurisdiction and priority from Low to Critical.</li>
                <li><b>The lifecycle, tracked</b>  pre-litigation, pleadings, discovery, trial, post-trial, appeal, settlement and archived.</li>
                <li><b>Who and where</b>  parties, assigned lawyer, wider team, external counsel, court name and case number.</li>
                <li><b>The dates that matter</b>  opened, closed, next hearing and next action deadline, each feeding the reminder engine.</li>
                <li><b>Classification and holds</b>  confidentiality up to attorney-client privileged, legal hold, tags and linked matters.</li>
              </ul>
            </div>

            <div className="lg-dark-block">
              <h3>2 · Contracts.  The full lifecycle</h3>
              <p>
                Contracts sit against the matter they belong to, with the terms and dates that decide what happens
                next.
              </p>
              <ul>
                <li><b>Every contract type</b>  service agreements, POs, NDAs, employment, licence and partnership agreements, each moving through active, expired, terminated or pending.</li>
                <li><b>The commercial substance</b>  parties, value, payment terms, governing law, signatories, execution date and termination reason.</li>
                <li><b>The dates that decide</b>  start, end and the renewal notice date, plus an auto-renew flag: the difference between a contract that lapses and one that rolls on is explicit, not assumed.</li>
                <li><b>Milestones</b> a per-contract schedule for obligations falling due during the term, not just at the end.</li>
                <li><b>Linked to the evidence</b>  documents, emails, tasks, risks and compliance records attach to the contract itself.</li>
              </ul>
            </div>

            <div className="lg-dark-block">
              <h3>3 · Deadlines that chase themselves</h3>
              <p>
                This is the part that pays for itself. pEntities watches the legal calendar hourly and starts a
                process when a date approaches no report to run, no one to remember.
              </p>
              <ul>
                <li><b>Six legal events</b>  a hearing approaching, a contract expiring, a renewal notice falling due, a compliance review due, a milestone due, a matter updated  each can start a workflow.</li>
                <li><b>Tuned to the team</b>  per-trigger notice periods (hearings 7 days, compliance 14, expiry and renewal 30), filterable by case or contract type.</li>
                <li><b>It knows what auto-renews</b>  renewal reminders skip contracts already set to auto-renew, so the alert means something.</li>
                <li><b>Once a day, not once an hour</b>  the engine checks hourly but remembers what it raised, so one alert per event per day.</li>
                <li><b>Then it acts</b>  email the parties, raise a Planner task, write to SharePoint, request an approval and wait for the answer.</li>
              </ul>
            </div>

            <div className="lg-dark-block">
              <h3>4 · Risk &amp; compliance, on the record</h3>
              <p>Tracked against the matter and contract they belong to, not in a separate spreadsheet.</p>
              <ul>
                <li><b>Risk assessments</b>  likelihood and impact per risk, with the portfolio plotted on a matrix colour-graded by concentration, so it's visible rather than asserted.</li>
                <li><b>Compliance obligations</b>  regulations tracked per matter with review and due dates; a review coming due starts a workflow.</li>
                <li><b>Notes</b>  a running rich-text record kept with the matter.</li>
              </ul>
            </div>

            <div className="lg-dark-block">
              <h3>5 · Documents, emails &amp; tasks  where they already live</h3>
              <p>No proprietary vault. pEntities links the evidence in place, from the Microsoft 365 you already run.</p>
              <ul>
                <li><b>Documents stay in SharePoint</b>  attached to a matter or contract and worked with in place, with version history, Purview retention labels and optional AI indexing.</li>
                <li><b>Email stays in Outlook</b>  attach the messages that evidence a matter and read them without leaving the record.</li>
                <li><b>Tasks are real Planner tasks</b>  with owner, dates, priority and progress, landing in that person's ordinary task list; completion tracks back to the matter.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Insight & Automation white */}
        <section id="insight" className="lg-light-section">
          <div className="case-management-container">
            <span className="lg-light-eyebrow">Insight &amp; Automation</span>
            <h2 className="lg-light-title">The shape of the whole book  and the process that runs itself</h2>
            <p className="lg-light-lead">
              Exposure across the portfolio, an assistant that briefs you from the live record, and no-code
              automation driven by the legal calendar.
            </p>

            <div className="lg-light-block">
              <h3>6 · Exposure &amp; portfolio reporting</h3>
              <figure className="lg-figure is-float is-left" onClick={() => this.openLightbox(legal2)}>
                <img loading="lazy" decoding="async" src={legal2} alt="Portfolio dashboards" />
                <figcaption>Portfolio dashboards matters by status, and the likelihood-by-impact risk matrix.</figcaption>
              </figure>
              <p>Legal leadership needs the shape of the whole book, not one matter at a time.</p>
              <ul>
                <li><b>The financial picture</b>  estimated exposure, settlements and fees per matter, rolled up across the portfolio and broken down by type, jurisdiction, status, lawyer or priority.</li>
                <li><b>Where risk concentrates</b>  the highest-exposure matters surfaced directly, so attention goes where the money is.</li>
                <li><b>How good the estimates were</b>  for closed matters, settled versus estimated: an honest measure of how well the team forecasts its own exposure.</li>
                <li><b>A single chronology per matter</b>   every dated event assembled into one ordered view, flagging what's coming and what's overdue.</li>
              </ul>
            </div>

            <div className="lg-light-block">
              <h3>7 · The AI legal assistant</h3>
              <figure className="lg-figure is-float" onClick={() => this.openLightbox(legal3)}>
                <img loading="lazy" decoding="async" src={legal3} alt="The AI legal panel" />
                <figcaption>The assistant opens with a legal overview drawn from the live record active matters, expiring contracts, risk and exposure.</figcaption>
              </figure>
              <p>
                Grounded in your own matters and running on your own Azure OpenAI configuration  the data it
                reasons over never leaves the tenant.
              </p>
              <ul>
                <li><b>It briefs you on arrival</b>  active matters, contracts expiring within 90 days, high-value and auto-renewing agreements, risk, compliance and exposure.</li>
                <li><b>Ask in plain language</b>  it queries the real record across 20+ filters: type, status, priority, jurisdiction, lawyer, party, exposure range, stage, legal hold, court and tags.</li>
                <li><b>Contracts, found properly</b>  by type, party, governing law, value range, what expires in a window, what auto-renews, what needs notice served.</li>
                <li><b>And the documents</b>  contracts read automatically by Azure Document Intelligence and indexed, so the assistant answers from their contents.</li>
              </ul>
            </div>

            <div className="lg-light-block">
              <h3>8 · Automation no-code legal workflows</h3>
              <p>The six legal events feed the platform's full automation engine, on a drag-and-drop canvas inside Teams.</p>
              <ul>
                <li><b>Draw the process</b>  conditions, switches, loops, parallel branches, variables, delays and reusable sub-workflows. No code, no premium automation tier.</li>
                <li><b>40+ actions</b>  email, SharePoint, document generation, Planner tasks, AI extraction and the five legal query tools.</li>
                <li><b>People in the loop</b>  approvals wait days or weeks with approved/rejected/timeout branches; links are signed and time-limited.</li>
                <li><b>Prove it before it goes live</b>  validation flags problems on the canvas, a dry run shows what each step resolved, and run history records every execution.</li>
                <li><b>Describe it and let AI build it</b>  plain-language problem in, feasibility-checked workflows out, ready to generate onto the canvas.</li>
              </ul>
            </div>

            <div className="lg-light-block">
              <h3>9 · Security, residency &amp; isolation</h3>
              <p>
                Matters, contracts and their evidence stay in your own Microsoft 365 and Azure tenant. Documents
                remain in SharePoint, email in Outlook and tasks in Planner, governed by the controls already in
                place. A workflow runs as the person who owns it with delegated permissions,  it can never reach
                data that person couldn't reach themselves. Workflow definitions and run history sit in per-tenant
                storage, and the assistant runs on your own Azure OpenAI  matter data is never sent to a shared
                service.
              </p>
            </div>
          </div>
        </section>

        {/* How pEntities compares dark */}
        <section id="compares" className="lg-compare-section">
          <div className="case-management-container">
            <span className="lg-compare-eyebrow">How pEntities compares</span>
            <h2 className="lg-compare-title">Against traditional legal and contract-management platforms</h2>
            <p className="lg-compare-lead">
              Dedicated legal platforms are capable products. The essential difference is one of approach: they ask
              the team to move its matters, documents and mail into a separate system and to work in a separate
              application. pEntities leaves everything where it already is and adds the legal layer over it,
              inside Teams.
            </p>

            <div className="lg-compare-table">
              <div className="lg-compare-row lg-compare-head">
                <div>Consideration</div>
                <div>pEntities</div>
                <div>Traditional legal platforms</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Where the evidence lives</div>
                <div data-label="pEntities">Stays in your SharePoint, Outlook and Planner  no migration</div>
                <div data-label="Traditional">Moved into a separate proprietary repository</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Where people work</div>
                <div data-label="pEntities">Inside Microsoft Teams, beside everything else</div>
                <div data-label="Traditional">A separate application to learn and log into</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Time to value</div>
                <div data-label="pEntities">Immediate  no migration and no retraining</div>
                <div data-label="Traditional">Weeks to months of setup, migration and training</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Deadlines</div>
                <div data-label="pEntities">Six legal events drive the full automation engine, with per-team notice periods</div>
                <div data-label="Traditional">Usually alerts only  automation is a separate module</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Data control</div>
                <div data-label="pEntities">Runs in your own tenant; AI on your own Azure OpenAI</div>
                <div data-label="Traditional">Vendor-hosted cloud, often outside the tenant</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Built-in AI</div>
                <div data-label="pEntities">Briefing assistant and plain-language querying included</div>
                <div data-label="Traditional">Typically a paid add-on, if available</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Breadth</div>
                <div data-label="pEntities">Legal sits alongside HR, Finance, documents and ticketing on one platform</div>
                <div data-label="Traditional">Legal only  integration with the rest is a project</div>
              </div>
              <div className="lg-compare-row">
                <div data-label="Consideration">Licensing</div>
                <div data-label="pEntities">A simple per-tenant subscription via the Azure Marketplace</div>
                <div data-label="Traditional">Per-seat licences plus module and connector fees</div>
              </div>
            </div>

            <p className="lg-compare-close">
              <b>In short:</b> because you already run on Microsoft 365, pEntities delivers the structure,
              visibility and automation of a dedicated legal platform without the migration, the separate
              application, or the loss of control over your own data.
            </p>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="case-management-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal,  court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision  IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Image Lightbox */}
        {this.state.lightboxSrc && (
          <div className="lg-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="lg-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="lg-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Footer */}
        <FaqSection path="/solutions/case-management" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default CaseManagement;

